import { faExternalLink, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';

import Debug from '@/components/Debug';
import RemitServiceLineCard from '@/components/RemitServiceLineCard';
import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import RemitContext from '@/pages/EditRemit/contexts/remitContext';
import { RemitServiceLineSchema } from '@/pages/EditRemit/schemas/RemitServiceLineSchema';
import RemitServiceLineRepository from '@/repositories/RemitServiceLineRepository';
import { ClaimStatusRegistry, RemitClaim, RemitServiceLinePayload } from '@/types/billing';
import Badge from '@/ui/components/Badge';
import Card from '@/ui/components/Card';
import CopyContent from '@/ui/components/CopyContent';
import Heading from '@/ui/components/Heading';
import Tooltip from '@/ui/components/Tooltip';
import ViewInCandid from '@/ui/components/ViewInCandid';
import { candidClaimsUrl } from '@/utils/candidLink';
import { currencyInCentsFormatter } from '@/utils/currencies';
import formatPersonName from '@/utils/formatPersonName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

import InPlaceEditor from '../PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { RecordType } from '../PersonInPlaceEditor/components/InPlaceEditor/types';
import { Field, FieldTypes } from '../PersonInPlaceEditor/types';

import { RemitClaimCardProps } from './types';

function isCandidPCN(patient_control_number: string | undefined) {
  return !Number.isInteger(Number(patient_control_number));
}

const RemitClaimCard = (props: RemitClaimCardProps) => {
  const { addMessage, host } = useContext(AppContext);
  const { refetch: refetchRemit, insurancePlans } = useContext(RemitContext);
  const { claim, highlight, editable } = props;
  const status = ClaimStatusRegistry[claim.status as keyof typeof ClaimStatusRegistry];
  const elementRef = useRef(null);

  useEffect(() => {
    if (elementRef.current && highlight) {
      (elementRef.current as HTMLElement).scrollIntoView({ behavior: 'smooth' });
    }
  }, [highlight]);
  const submittedRemitServiceLineValuesManipulator = async (
    values: Partial<RemitServiceLinePayload>
  ) => {
    return values as RemitServiceLinePayload;
  };
  const remitServiceLineRepo = RemitServiceLineRepository();

  const mutateRemitServiceLine = useMutation({
    mutationFn: async ({
      id,
      values
    }: {
      id?: string;
      values: Partial<RemitServiceLinePayload>;
    }) => {
      if (!id) {
        return remitServiceLineRepo.create({ data: values as RemitServiceLinePayload });
      }
      return await remitServiceLineRepo.update({ id, data: values as RemitServiceLinePayload });
    },
    onSuccess: async () => {
      if (refetchRemit) await refetchRemit();
      addMessage('success', 'Remit Service Line saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteRemitServiceLine = useMutation({
    mutationFn: async (id: string) => {
      await remitServiceLineRepo.delete({ id });
    },
    onSuccess: async () => {
      if (refetchRemit) await refetchRemit();
      addMessage('success', 'Remit Service Line deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });
  const fields: Field<RemitServiceLinePayload>[] = [
    {
      alwaysSend: true,
      key: 'remit_claim',
      label: 'Remit Claim',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'start_on',
      label: 'Start On',
      type: FieldTypes.DATE
    },
    {
      key: 'end_on',
      label: 'End On',
      type: FieldTypes.DATE
    },
    {
      key: 'code',
      label: 'Code',
      type: FieldTypes.TEXT
    },
    {
      key: 'modifier',
      label: 'Modifier',
      type: FieldTypes.TEXT
    },
    {
      key: 'qualifier',
      label: 'Qualifier',
      type: FieldTypes.TEXT
    },
    {
      key: 'allowed_units',
      label: 'Allowed Units',
      type: FieldTypes.NUMBER
    },
    {
      key: 'billed_units',
      label: 'Billed Units',
      type: FieldTypes.NUMBER
    },
    {
      key: 'charge_amount',
      label: 'Charge Amount',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'paid_amount',
      label: 'Paid Amount',
      type: FieldTypes.CURRENCY
    },
    {
      key: 'revenue_code',
      label: 'Revenue Code',
      type: FieldTypes.TEXT
    }
  ];

  const payerIDs: string[] = useMemo(
    () =>
      [claim.financial_info?.payer_identifier, claim.financial_info?.other_payer_identifier].filter(
        (payerID): payerID is string => Boolean(String(payerID?.trim()))
      ),
    [claim.financial_info]
  );

  const payerName = useMemo(() => {
    const payerNameFromPayerID = payerIDs.reduce((acc, payerID): string => {
      const insurancePlan = insurancePlans[payerID];
      return insurancePlan?.payer_name || acc;
    }, '');

    return payerNameFromPayerID || claim.financial_info?.payer_name || 'Unknown Payer';
  }, [claim.financial_info?.payer_name, insurancePlans, payerIDs]);

  const renderClaimStatementPeriod = (claim: RemitClaim) => {
    if (!claim.claim_statement_period_start_on || !claim.claim_statement_period_end_on) {
      return null;
    }

    if (claim.claim_statement_period_start_on === claim.claim_statement_period_end_on) {
      return <>{claim.claim_statement_period_start_on}</>;
    }

    return (
      <>
        {claim.claim_statement_period_start_on} - {claim.claim_statement_period_end_on}
      </>
    );
  };

  return (
    <Card className={`relative ${highlight ? 'bg-yellow-50' : ''} border shadow-xs`}>
      <div className="flex gap-4 items-center border-b-2" data-testid="claimHeader">
        <Heading level={4} className="mb-2">
          <Link to={`/billing/claims/by_external_id/${claim.patient_control_number}`} className="">
            {claim.patient_control_number}
            <FontAwesomeIcon className="text-blue-600 ml-2 text-xs" icon={faExternalLink} />
          </Link>
        </Heading>
        {isCandidPCN(claim.patient_control_number) && (
          <ViewInCandid url={candidClaimsUrl(host, claim.patient_control_number || '')} />
        )}

        {status && (
          <Badge
            color={status.level == 'alert' ? Color.RED : Color.BLUE}
            className="p-0 m-0 text-xs font-normal ">
            {status.description}
          </Badge>
        )}
        {claim.healthie_cms_1500 && (
          <Badge color={Color.GREEN} className="p-0 m-0 text-xs font-normal">
            <Link to={`/billing/claims/${claim.healthie_cms_1500}`} className="">
              Linked Claim
              <FontAwesomeIcon className="text-green-800 ml-2 text-xs" icon={faExternalLink} />
            </Link>
          </Badge>
        )}
        <div className="">
          <div className="text-gray-800">
            <Tooltip
              tooltipContent={
                <div>
                  <div>Name: {claim.financial_info?.payer_name}</div>
                  {payerIDs ? (
                    <div>
                      Payer IDs:
                      {payerIDs.map((payerID, i) => (
                        <div key={payerID || i}>{payerID}</div>
                      ))}
                    </div>
                  ) : null}

                  <div>
                    Address:
                    {claim.financial_info?.payer_address_line1 &&
                      ` ${claim.financial_info?.payer_address_line1}`}
                    {claim.financial_info?.payer_address_line2 &&
                      ` ${claim.financial_info?.payer_address_line2}`}
                    {claim.financial_info?.payer_city && ` ${claim.financial_info?.payer_city}`}
                    {claim.financial_info?.payer_state && ` ${claim.financial_info?.payer_state}`}
                    {claim.financial_info?.payer_zip && ` ${claim.financial_info?.payer_zip}`}
                  </div>
                </div>
              }>
              {payerName} <FontAwesomeIcon icon={faInfoCircle} />
            </Tooltip>
          </div>
        </div>
      </div>
      <div ref={elementRef} className={`grid grid-cols-5 `} data-testid="claimDetails">
        <div className="">
          <div className="text-sm text-gray-600">Claim Period</div>
          <div>{renderClaimStatementPeriod(claim)}</div>
        </div>

        <div className="">
          <div className="text-sm text-gray-600">
            Patient <FontAwesomeIcon icon={faInfoCircle} />
          </div>
          <div>
            <Tooltip
              tooltipContent={
                <div>
                  <div>
                    Patient:{' '}
                    {[
                      claim.patient_name_prefix,
                      claim.patient_first_name,
                      claim.patient_middle_name,
                      claim.patient_last_name,
                      claim.patient_name_suffix
                    ].join(' ')}
                  </div>
                  {claim.patient_id && (
                    <div>
                      Patient ID: {claim.patient_id} ({claim.patient_id_qualifier})
                    </div>
                  )}
                  {Boolean(
                    [
                      claim.subscriber_prefix,
                      claim.subscriber_first_name,
                      claim.subscriber_middle_name,
                      claim.subscriber_last_name,
                      claim.subscriber_suffix
                    ].join('')
                  ) && (
                    <div>
                      Subscriber:{' '}
                      {[
                        claim.subscriber_prefix,
                        claim.subscriber_first_name,
                        claim.subscriber_middle_name,
                        claim.subscriber_last_name,
                        claim.subscriber_suffix
                      ].join(' ') || 'Self'}
                      {claim.subscriber_id && (
                        <div>
                          Subscriber ID: {claim.subscriber_id} ({claim.subscriber_id_qualifier})
                        </div>
                      )}
                      {claim.subscriber_patient_relationship && (
                        <div> ({claim.subscriber_patient_relationship})</div>
                      )}
                    </div>
                  )}
                </div>
              }>
              {claim.person && formatPersonName({ person: claim.person })}
            </Tooltip>
          </div>
        </div>

        <div className="">
          <div className="text-sm text-gray-600">Charge Amt</div>
          <div>{currencyInCentsFormatter(claim.charge_amount_in_cents || 0)}</div>
        </div>

        <div className="">
          <div className="text-sm text-gray-600">Payment Amt</div>
          <div>{currencyInCentsFormatter(claim.paid_amount_in_cents || 0)}</div>
        </div>

        <div className="">
          <div className="text-sm text-gray-600">Payer Claim #</div>
          <div>
            <CopyContent
              content={`${claim.payer_claim_control_number}`}
              copyButtonClassName="block"
              wrapperClassName="truncate ellipsis block max-w-xs"
            />
          </div>
        </div>

        <Debug object={claim as object} />
      </div>
      <div data-testid="service-lines" className="relative">
        {claim.service_lines.map((serviceLine) => (
          <InPlaceEditor
            doubleClickToEdit={false}
            deleteMutation={deleteRemitServiceLine}
            disableEdit={!editable}
            fields={fields}
            key={serviceLine.id}
            mutation={mutateRemitServiceLine}
            record={
              {
                ...serviceLine,
                remit_claim: claim.id
              } as RemitServiceLinePayload
            }
            recordType={RecordType.RemitClaim}
            schema={RemitServiceLineSchema}
            submittedValuesManipulator={submittedRemitServiceLineValuesManipulator}
            subsectionEditor>
            <RemitServiceLineCard serviceLine={serviceLine} editable={editable} />
          </InPlaceEditor>
        ))}
        <div className="my-1">
          {editable && (
            <InPlaceEditor
              deleteMutation={deleteRemitServiceLine}
              fields={fields}
              mutation={mutateRemitServiceLine}
              record={
                {
                  remit_claim: claim.id
                } as RemitServiceLinePayload
              }
              recordType={RecordType.RemitClaim}
              schema={RemitServiceLineSchema}
              newRecord={true}
              newRecordButtonText="Add Service Line"
              submittedValuesManipulator={submittedRemitServiceLineValuesManipulator}
              subsectionEditor
            />
          )}
        </div>
      </div>
    </Card>
  );
};

export default RemitClaimCard;
